import React, { useEffect, useState } from "react";
import { CustomCalendar } from "../../../../components/CustomCalendar/CustomCalendar";
import { EventPopup } from "../../../../components/CustomCalendar/EventPopup";
import { useGetCorporateEngagementsCalendarQuery } from "../../../../redux/service/modules/dashboard";
import { format, startOfMonth, endOfMonth, startOfWeek, endOfWeek, startOfDay, endOfDay } from "date-fns";
import Loader from "../../../../components/common/loader";
import TableLayout from "../../../../components/layout/TableLayout";
import { useDispatch } from "react-redux";
import { setDefaultFilterValues } from "../../../../redux/slice/filterSlice";

export const CorporateAccessEvents = ({
    appliedFilters,
    setRequiredFilters,
}) => {
    const [currentDate, setCurrentDate] = useState(new Date());
    const [currentView, setCurrentView] = useState('month');
    const [selectedEvent, setSelectedEvent] = useState(null);
    const [popupPosition, setPopupPosition] = useState({ x: 0, y: 0 });
    const dispatch = useDispatch();

    const getDateRange = (date, view) => {
        switch (view) {
            case 'month':
                return {
                    startDate: format(startOfMonth(date), "dd-MM-yyyy"),
                    endDate: format(endOfMonth(date), "dd-MM-yyyy")
                };
            case 'week':
                return {
                    startDate: format(startOfWeek(date), "dd-MM-yyyy"),
                    endDate: format(endOfWeek(date), "dd-MM-yyyy")
                };
            case 'day':
                return {
                    startDate: format(startOfDay(date), "dd-MM-yyyy"),
                    endDate: format(endOfDay(date), "dd-MM-yyyy")
                };
            default:
                return {
                    startDate: format(startOfMonth(date), "dd-MM-yyyy"),
                    endDate: format(endOfMonth(date), "dd-MM-yyyy")
                };
        }
    };

    const dateRange = getDateRange(currentDate, currentView);

    const { data: calendarEvents, isFetching } = useGetCorporateEngagementsCalendarQuery({
        startDate: dateRange.startDate,
        endDate: dateRange.endDate,
        appliedFilters,
    });

    useEffect(() => {
        if (calendarEvents?.defaultFilterValues) {
            dispatch(
                setDefaultFilterValues({
                    defaultFilterValues: calendarEvents.defaultFilterValues,
                })
            );
        }
        setRequiredFilters(["group", "analyst"]);
        return () => {
            setRequiredFilters([]);
        };
    }, [calendarEvents]);

    const events =
        Array.isArray(calendarEvents?.data)
            ? calendarEvents.data.map((event) => ({
                id: event.id,
                title: event.title,
                start: new Date(event.start),
                end: new Date(event.end),
                username: event.username,
                email: event.email,
                role: event.role,
                engagementGroup: event.engagement_group,
                sector: Array.isArray(event.sectors)
                    ? event.sectors
                    : event.sectors?.split(",").map((sector) => sector.trim()) ||
                      [],
                corporate: event.corporate,
                fundClients: Array.isArray(event.fund_clients)
                    ? event.fund_clients
                    : event.fund_clients
                          ?.split(",")
                          .map((client) => client.trim()) || [],
                accountManager: event.account_manager,
                eventType: event.extendedProps.event_type,
                eventName: event.extendedProps.event_name,
                meetingType: event.extendedProps.meeting_type,
                city: event.extendedProps.city,
            }))
            : [];

    const handleSelectEvent = (event, e) => {
        const rect = e.currentTarget.getBoundingClientRect();
        setPopupPosition({
            x: rect.left,
            y: rect.bottom + window.scrollY,
        });
        setSelectedEvent(event);
    };

    const handleNavigate = (newDate) => {
        setCurrentDate(newDate);
    };

    const handleViewChange = (newView) => {
        setCurrentView(newView);
    };

    return (
        <TableLayout>
            <div style={{ position: 'relative', minHeight: '500px' }}>
                {isFetching && (
                    <div style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        backgroundColor: 'rgba(255, 255, 255, 0.7)',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        zIndex: 1000
                    }}>
                        <Loader />
                    </div>
                )}
                <CustomCalendar
                    events={events}
                    onSelectEvent={handleSelectEvent}
                    onNavigate={handleNavigate}
                    view={currentView}
                    onView={handleViewChange}
                />

                {selectedEvent && (
                    <EventPopup
                        event={selectedEvent}
                        onClose={() => setSelectedEvent(null)}
                        position={popupPosition}
                        type="corporate"
                    />
                )}
            </div>
        </TableLayout>
    );
};
